import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { CTAButton } from '@/components/ui/cta-button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Mail, User, Building2, Phone, MessageSquare } from 'lucide-react';

const contactFormSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  email: z.string().email("Por favor ingresa un email válido"),
  profile: z.enum(["empresa", "candidato"]).optional(),
  phone: z.string().optional(),
  message: z.string().min(10, "El mensaje debe tener al menos 10 caracteres"),
  website: z.string().optional(), // honeypot field
});

type ContactFormData = z.infer<typeof contactFormSchema>;

interface ContactFormProps {
  type?: 'contact' | 'demo' | 'consultation' | 'cv-registration';
  title?: string;
  subtitle?: string;
  className?: string;
  onSuccess?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ 
  type = 'contact',
  title,
  subtitle,
  className = "",
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      profile: undefined,
      phone: '',
      message: '',
      website: '', // honeypot field
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          ...data,
          type
        }
      });

      if (error) {
        console.error('Error sending email:', error);
        throw error;
      }

      const successMessages = {
        contact: "Hemos recibido tu mensaje. Te contactaremos pronto.",
        demo: "Tu solicitud de demo ha sido enviada. Nuestro equipo se contactará contigo.",
        consultation: "Hemos recibido tu solicitud. Un ejecutivo se contactará contigo pronto.",
        'cv-registration': "Tu perfil ha sido registrado exitosamente. Te notificaremos sobre oportunidades."
      };

      toast({
        title: "¡Mensaje enviado!",
        description: successMessages[type],
      });
      
      form.reset();
      onSuccess?.();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error al enviar",
        description: "Hubo un problema al enviar tu mensaje. Inténtalo nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFormTitle = () => {
    if (title) return title;
    switch (type) {
      case 'demo':
        return 'Solicitar una demostración';
      case 'consultation':
        return 'Conversar con un ejecutivo';
      case 'cv-registration':
        return 'Registrar mi hoja de vida';
      default:
        return '¿No encontraste todo lo que buscabas?';
    }
  };

  const getFormSubtitle = () => {
    if (subtitle) return subtitle;
    switch (type) {
      case 'demo':
        return 'Descubra cómo nuestra plataforma puede transformar su proceso de reclutamiento';
      case 'consultation':
        return 'Nuestro equipo comercial está listo para ayudarle';
      case 'cv-registration':
        return 'Cree su perfil profesional y acceda a oportunidades exclusivas';
      default:
        return 'Escríbanos a través de este formulario. ¡Nuestro equipo recibirá su consulta y le responderá muy pronto!';
    }
  };

  return (
    <div className={`max-w-2xl mx-auto ${className}`}>
      <div className="text-center mb-8">
        <h2 className="text-xl md:text-2xl font-bold mb-3 text-gradient">
          {getFormTitle()}
        </h2>
        <p className="mb-5 text-muted-foreground">{getFormSubtitle()}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nombre completo *
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Su nombre completo" 
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email *
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="tu@email.com" 
                      type="email"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="profile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Perfil
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isSubmitting}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione su perfil" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="empresa">Empresa</SelectItem>
                      <SelectItem value="candidato">Candidato</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Teléfono
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="+506 8888-8888" 
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Mensaje *
                </FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Cuéntenos en qué podemos ayudarle..."
                    className="min-h-[120px]"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Honeypot field - hidden from users but visible to bots */}
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem className="hidden">
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Don't fill this field"
                    {...field}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <CTAButton 
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Enviar mensaje
              </>
            )}
          </CTAButton>

          <p className="text-xs text-muted-foreground text-center">
            * Campos obligatorios. Al enviar este formulario acepta nuestros{" "}
            <a href="/terminos-y-condiciones" className="text-primary hover:underline">
              términos y condiciones
            </a>.
          </p>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;